import type { ComponentType } from 'react';
import {
  AlertTriangle,
  Check,
  CircleOff,
  Ellipsis,
  FileUp,
  LoaderCircle as ProcessingCircleCog,
  X,
} from 'lucide-react';
import type { PatientStatus } from '../types';

type StatusDisplayConfig = {
  icon: ComponentType<{ className?: string }>;
  iconClassName: string;
  badgeClassName: string;
};

const STATUS_CONFIGS: Array<
  StatusDisplayConfig & { statusIncluded: PatientStatus[] }
> = [
  {
    icon: Check,
    iconClassName: 'text-green-800',
    badgeClassName: 'bg-green-400 text-black',
    statusIncluded: ['COMPLETED', 'RU_COMPLETED', 'FS_COMPLETE'],
  },
  {
    icon: ProcessingCircleCog,
    iconClassName: 'text-cyan-800',
    badgeClassName: 'bg-cyan-300 text-black',
    statusIncluded: ['PROCESSING', 'RU_PROCESSING', 'FS_PROCESSING'],
  },
  {
    icon: Ellipsis,
    iconClassName: 'text-cyan-800',
    badgeClassName: 'bg-cyan-300 text-black',
    statusIncluded: ['OPEN', 'FS_PENDING'],
  },
  {
    icon: FileUp,
    iconClassName: 'text-cyan-500',
    badgeClassName: 'bg-cyan-300 text-black',
    statusIncluded: ['RU_OPEN'],
  },
  {
    icon: AlertTriangle,
    iconClassName: 'text-amber-800',
    badgeClassName: 'bg-amber-300 text-black',
    statusIncluded: ['ACTION REQUIRED'],
  },
  {
    icon: CircleOff,
    iconClassName: 'text-black',
    badgeClassName: 'bg-neutral-200 text-black',
    statusIncluded: ['CANCELLED'],
  },
  {
    icon: X,
    iconClassName: 'text-white',
    badgeClassName: 'bg-red-500 text-white',
    statusIncluded: ['CLOSED', 'FS_CLOSED'],
  },
];

export const DEFAULT_STATUS_CONFIG = {
  icon: Check,
  iconClassName: 'text-gray-800',
  badgeClassName: 'bg-gray-300 text-black',
} as const satisfies StatusDisplayConfig;

export const getStatusDisplayConfig = (
  status: PatientStatus | string,
): StatusDisplayConfig => {
  const upperStatus = status.toUpperCase();
  const config = STATUS_CONFIGS.find((c) =>
    c.statusIncluded.includes(upperStatus as PatientStatus),
  );
  if (config) return config;

  return {
    icon: DEFAULT_STATUS_CONFIG.icon,
    badgeClassName: DEFAULT_STATUS_CONFIG.badgeClassName,
    iconClassName: DEFAULT_STATUS_CONFIG.iconClassName,
  };
};
